/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Button } from '@/components/ui/button';
import { Info, GitBranch, FileText, Network, ClipboardCheck } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type ProductFocusTab = 'overview' | 'source-control' | 'documentation' | 'architecture' | 'testing';

interface ProductFocusTabMenuProps {
  activeTab: ProductFocusTab;
  onTabChange: (tab: ProductFocusTab) => void;
}

export function ProductFocusTabMenu({ activeTab, onTabChange }: ProductFocusTabMenuProps) {
  const tabConfig: Record<ProductFocusTab, { label: string; icon: React.ReactNode }> = {
    'overview': { label: 'Overview', icon: <Info className="h-5 w-5" /> },
    'source-control': { label: 'Source Control', icon: <GitBranch className="h-5 w-5" /> },
    'documentation': { label: 'Documentation', icon: <FileText className="h-5 w-5" /> },
    'architecture': { label: 'Architecture Diagram', icon: <Network className="h-5 w-5" /> },
    'testing': { label: 'Testing', icon: <ClipboardCheck className="h-5 w-5" /> },
  };

  const availableTabs: ProductFocusTab[] = ['overview', 'source-control', 'documentation', 'architecture', 'testing'];

  return (
    <TooltipProvider>
      <div className="flex flex-col space-y-3 items-center w-[20px]">
        {availableTabs.map(tab => (
          <Tooltip key={tab} delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                variant={activeTab === tab ? 'default' : 'ghost'}
                size="icon"
                className="h-10 w-10 rounded-full"
                onClick={() => onTabChange(tab)}
              >
                {tabConfig[tab].icon}
                <span className="sr-only">{tabConfig[tab].label}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{tabConfig[tab].label}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}

